import { DeviceReadingResponseDto, EventValueRange } from '@thingslog/repositories';
import React, { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { Map as MapIcon, ShowChart } from '@mui/icons-material';
import { SensorsDataChart } from './SensorsDataChart';
import { SensorsDataMap } from './SensorsDataMap';
import { ChartLoadingSkeleton } from '../skeletons/ChartLoadingSkeleton';
import { GnssDeviceReadingResponseDto } from '@thingslog/repositories/src/deviceCounter/DeviceReadingsResponseDto';

interface SensorsDataVisualizerProps {
  selectedTab: 'chart' | 'map';
  onSelectedTabChange: (tab: 'chart' | 'map') => void;
  data: DeviceReadingResponseDto[];
  eventValueRanges: EventValueRange[];
  displayEventRanges?: boolean;
  isLoading?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`visualizer-tabpanel-${index}`}
      aria-labelledby={`visualizer-tab-${index}`}
      {...other}
      style={{ height: '100%', display: value === index ? 'block' : 'none' }}
    >
      {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
    </div>
  );
}

export const SensorsDataVisualizer: FC<SensorsDataVisualizerProps> = ({
  selectedTab,
  onSelectedTabChange,
  data,
  eventValueRanges,
  displayEventRanges = true,
  isLoading = false,
}: SensorsDataVisualizerProps) => {
  const numericData = useMemo(() => {
    return data.filter((item: DeviceReadingResponseDto) => item.sensorData === 'NUMERIC');
  }, [data]);

  const mapData: GnssDeviceReadingResponseDto[] = useMemo(() => {
    return data.filter(
      (item: DeviceReadingResponseDto) => item.sensorData === 'GNSS'
    ) as GnssDeviceReadingResponseDto[];
  }, [data]);

  const shouldDisplayNumericOption = useMemo(() => {
    return numericData.length > 0;
  }, [numericData]);

  const shouldDisplayMapOption = useMemo(() => {
    return mapData.length > 0;
  }, [mapData]);

  useEffect(() => {
    if (!isLoading) {
      if (shouldDisplayMapOption && !shouldDisplayNumericOption) {
        onSelectedTabChange('map');
      } else if (shouldDisplayNumericOption) {
        onSelectedTabChange('chart');
      }
    }
  }, [shouldDisplayNumericOption, shouldDisplayMapOption, isLoading]);

  const handleTabChange = (event: SyntheticEvent, newValue: 'chart' | 'map'): void => {
    onSelectedTabChange(newValue);
  };

  if (isLoading) {
    return (
      <div className="h-[500px]">
        <ChartLoadingSkeleton />
      </div>
    );
  }

  const hasBothOptions = shouldDisplayNumericOption && shouldDisplayMapOption;

  // If we have neither, we can show the Chart component which handles "No Data" state nicely
  if (!shouldDisplayNumericOption && !shouldDisplayMapOption) {
    return (
      <SensorsDataChart
        data={data}
        eventValueRanges={eventValueRanges}
        displayEventRanges={displayEventRanges}
        isLoading={isLoading}
      />
    );
  }

  return (
    <Paper className="flex flex-col h-full w-full" elevation={0}>
      {hasBothOptions && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label="sensors data visualization tabs"
          >
            <Tab icon={<ShowChart />} iconPosition="start" label="Chart" value="chart" />
            <Tab icon={<MapIcon />} iconPosition="start" label="Map" value="map" />
          </Tabs>
        </Box>
      )}

      <Box className="flex-1 overflow-hidden">
        {(selectedTab === 'chart' || (!hasBothOptions && shouldDisplayNumericOption)) && (
          <TabPanel value="chart" index="chart">
            <div className="h-full">
              <SensorsDataChart
                data={data}
                eventValueRanges={eventValueRanges}
                displayEventRanges={displayEventRanges}
                isLoading={isLoading}
              />
            </div>
          </TabPanel>
        )}

        {(selectedTab === 'map' || (!hasBothOptions && shouldDisplayMapOption)) && (
          <TabPanel value="map" index="map">
            <div className="h-full">
              <SensorsDataMap data={mapData} isLoading={isLoading} />
            </div>
          </TabPanel>
        )}
      </Box>
    </Paper>
  );
};
